import { useMemo } from "react";

import qs from "qs";

import { useShipmentFiltersContext } from "./useShipmentFiltersContext";
import { useShipmentsUrl } from "./useShipmentsUrl";
import { useSwrFetch } from "./useSwrFetch";

interface GetCondensedShipmentsResponse {
  results: CondensedShipment[];
  meta: ShipmentMeta;
}

interface ShipmentMeta {
  count: number;
}

interface PaginationProps {
  page: number;
  pageSize: number;
}

const buildShipmentTypeFilter = (shipmentTypes: string[] | undefined) => {
  if (shipmentTypes?.find((type) => type === "ALL")) {
    return null;
  }
  return shipmentTypes;
};

const buildFilterQueryParameters = (
  filters: ShipmentFilters | null,
  offset: number | null,
  limit: number | null,
) => {
  const filtersObject = {
    equipment_type: filters?.equipment_types ?? null,
    service: filters?.services ?? null,
    commodity: filters?.commodity?.code ?? null,
    status: filters?.status ?? null,
    order_by: filters?.order_by ?? null,
    origin_province: filters?.origin?.provinces ?? null,
    origin_country: filters?.origin?.countries ?? null,
    destination_province: filters?.destination?.provinces ?? null,
    destination_country: filters?.destination?.countries ?? null,
    offset: offset ?? null,
    limit: limit ?? null,
    shipment_name:
      filters?.shipmentName && filters.shipmentName !== ""
        ? filters.shipmentName
        : null,
    shipment_type: buildShipmentTypeFilter(filters?.shipmentTypes),
    include_branch_shipments: filters?.includeBranchShipments ? "1" : null,
    include_company_shipments: filters?.includeCompanyShipments ? "1" : null,
    branches: filters?.branches ?? null,
    shippers: filters?.shippers ?? null,
    shipper_business_type: filters?.shipperBusinessType ?? null,
  };

  return qs.stringify(filtersObject, {
    skipNulls: true,
    arrayFormat: "repeat",
  });
};

export function useShipments(props?: PaginationProps) {
  let offset: number | null = null;
  let limit: number | null = null;
  if (props !== undefined) {
    const { page, pageSize } = props;
    limit = pageSize;
    offset = (page - 1) * limit;
  }

  const { url: baseUrl } = useShipmentsUrl();
  const { filters } = useShipmentFiltersContext();

  // Build the full URL with query parameters
  const url = useMemo(() => {
    if (!baseUrl) return null;
    const filtersQueryString = buildFilterQueryParameters(
      filters,
      offset,
      limit,
    );
    return `${baseUrl}?${filtersQueryString}`;
  }, [baseUrl, filters, offset, limit]);

  const {
    data,
    loading,
    error: swrError,
    refetch,
  } = useSwrFetch<GetCondensedShipmentsResponse>({
    url,
    errorMessage: "Failed to fetch shipments. Please contact us.",
    errorMessageKey: "detail",
  });

  // Extract the error message properly
  const error = useMemo(() => {
    if (!swrError) return null;
    // SWR error is already processed by useSwrFetch's extractAPIRequestErrorMessage
    return swrError;
  }, [swrError]);

  // `data` is undefined whenever a refetch is in flight, because the
  // revalidation helpers in utils/swr/swr.ts blank the cache entry before
  // refetching. A bare `?? []` would hand back a fresh array identity on every
  // one of those renders, spinning any effect that depends on it.
  const shipments = useMemo(() => data?.results ?? [], [data]);

  return {
    shipments,
    isLoading: loading,
    error,
    count: data?.meta?.count ?? 0,
    refreshShipments: refetch,
  };
}
